import { OnInit, OnDestroy, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Product } from '../../../../../common/js/static/Product';
import { CoverpathUtils } from '../../../../../coverpath-common/js/static/CoverpathUtils';
import { WorkflowService } from '../../../../../coverpath-common/angular/services/workflow/workflow.service';
import type { Policy } from '../../../../../common/js/customer/Policy';
import { WindowService } from '../../../../../common/angular-shared/services/window/window.service';
import {
  CloseActionTypes,
  ToastService,
} from '../../../../../coverpath-common/angular/components/overlays/toaster/toast-service.service';
import { ToasterIntent } from '../../../../../coverpath-common/angular/components/overlays/toaster/toaster.component';
import { ClientReapplyResponseType } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';

@Directive()
export abstract class BaseTransitionCardComponent implements OnInit, OnDestroy {
  controller;
  headerText: string;
  bodyText: string;
  processText: string;
  contactNumber: string;
  contactEmail: string;

  showReapplyCTA: boolean = false;
  showExitCTA: boolean = false;
  showDeclinedCTA: boolean = false;
  showRejectedCTA: boolean = false;
  showTerminatedCTA: boolean = false;
  showContactCTA: boolean = false;
  showChatCTA: boolean = false;
  showAddFileCTA: boolean = false;
  showLapsedCTA: boolean = false;
  showLoadingMsg: boolean = false;
  showEditCTA: boolean = false;
  showSupportFooter: boolean = false;
  coverpathSupportPhone: string = Product.channels.CAS.phone;
  rejectedCause: string;
  is12Pay: boolean = false;
  hasSubmissionValidationFailed: boolean = false;
  currentWorkflowStage: string;

  private workflowStateSubscription: Subscription;
  private workflowService: WorkflowService;

  //Toggles whether we should show a processing spinner on the card itself
  // (Note: this is different than the full page spinner)
  isProcessing: boolean = false;

  constructor(
    protected amorphicService: AmorphicService,
    public accountCenterService: AccountCenterService,
    protected windowService: WindowService,
    protected router: Router,
    protected toastService: ToastService
  ) {
    this.controller = amorphicService.controller;
  }

  ngOnInit(): void {
    this.workflowService = this.accountCenterService.workflowService;
    this.workflowStateSubscription = this.accountCenterService.workflowStateSubject.subscribe((workflowStage) => {
      this.currentWorkflowStage = workflowStage;
      this.constructCard(workflowStage);
    });

    this.is12Pay = this.controller.customer.application.primaryPolicy.term === Product.productSpecs.CPL12.term;
  }

  ngOnDestroy(): void {
    if (this.workflowStateSubscription) {
      this.workflowStateSubscription.unsubscribe();
    }
  }

  private setProcessingStatus(workflowStage: string): void {
    const [isEsignError] = this.getEsignStatus();
    const policy = this.controller.customer.application.primaryPolicy;
    const idState = policy.isAnnuity() ? policy.annuitant.idState : policy.insured.person.idState;
    switch (workflowStage) {
      case 'TLIC Application:Waiting for Signature':
      case 'Accelerated Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
        this.processText = '';
        this.isProcessing = !isEsignError;
        break;
      case 'Loading:loading':
      case 'Send to Underwriting:Send to underwriting':
        this.processText = 'Getting started';
        this.isProcessing = true;
        break;
      case 'Consent To Underwriting:Check Underwriting Consent Requirements':
      case 'Tele CMI:Check Tele CMI Requirements':
      case 'License-verification:Verifying':
      case 'Underwriting:Underwriting':
        this.processText = 'Reviewing your application';
        this.isProcessing = true;
        break;
      case 'Consent To Underwriting:Consent To Underwriting':
      case 'Tele CMI:Tele CMI Needed':
        this.isProcessing = false;
        break;
      case 'TLIC Application:Checking ID':
      case 'TLIC Application:Checking Insured ID':
      case 'TLIC Application:Checking Owner ID':
        this.processText = 'Checking your ID';
        this.isProcessing = true;
        break;
      case 'TLIC Application:Gathering Data':
        if (idState !== 'HI') {
          this.processText = 'Checking your prescription history';
          this.isProcessing = true;
        }
        break;
      case 'Accelerated Application:Determine Underwriting Class':
        this.processText = 'Checking your driving records';
        this.isProcessing = true;
        break;
      case 'License-verification:Verifying Fluidless':
        this.processText = 'Reviewing your other insurance policies';
        this.isProcessing = true;
        break;
      case 'TLIC Application:Determine Underwriting Class':
      case 'TLIC Application:Initial Underwriting':
        this.processText = 'Wrapping up';
        this.isProcessing = true;
        break;
      case 'Validate Coverpath Policies:Verifying application data':
        if (this.hasSubmissionValidationFailed) {
          this.processText = '';
          this.isProcessing = false;
        } else {
          this.processText = 'Validating your policy information';
          this.isProcessing = true;
          this.updateHasSubmissionValidationFailed(policy);
        }
        break;
      case 'Pre-application:Submitted':
        this.isProcessing = true;
        break;
      /**
       * Annuity Workflow Stages
       */
      case 'Canceled:Canceled':
        this.isProcessing = false;
        break;
      case 'Canceled:Timeout':
        this.isProcessing = false;
        break;
      case 'Rejected:Suitability Rejected':
        this.isProcessing = false;
        break;
      default:
        return null;
    }
  }

  private getHeaderText(workflowStage: string): string {
    const policy = this.controller.customer.application.primaryPolicy;
    const [isEsignError, ownerSignComplete, txtEsignError] = this.getEsignStatus();
    const idState = policy.isAnnuity() ? policy.annuitant.idState : policy.insured.person.idState;
    const altSubmitSignVerbiageEnabled = this.controller?.altSubmitSignVerbiageEnabled;
    switch (workflowStage) {
      case 'TLIC Application:Waiting for Signature':
      case 'Accelerated Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
        if (isEsignError) {
          // SSN validation failure error capture as part of HL-41263
          if (txtEsignError && txtEsignError === 'RECIPIENT_HAS_FAILED_SECURITY_CHECK') {
            return 'Sorry, we couldn’t validate your Social Security number.';
          }
          return 'Sorry, we are having trouble generating your documents.';
        } else {
          if (ownerSignComplete) {
            return 'Thank you for eSigning your documents.';
          }
          return "We're generating your documents so you can start signing...";
        }
      case 'Consent To Underwriting:Consent To Underwriting':
        if (this.controller.customer.application.primaryPolicy.insured.hasSigner) {
          return "We requested the signer's consent on behalf of the insured";
        } else {
          return "We requested the insured's consent";
        }
      case 'Tele CMI:Tele CMI Needed':
        return this.controller.customer.application.primaryPolicy.isOwnerSameAsInsured()
          ? 'Next step: complete your phone interview.'
          : 'Next step: have the insured complete the phone interview.';
      case 'Pre-application:Submitted':
      case 'Loading:loading':
      case 'Consent To Underwriting:Check Underwriting Consent Requirements':
      case 'Tele CMI:Check Tele CMI Requirements':
      case 'License-verification:Verifying':
      case 'Send to Underwriting:Send to underwriting':
      case 'Underwriting:Underwriting':
      case 'TLIC Application:Determine Underwriting Class':
      case 'TLIC Application:Checking ID':
      case 'TLIC Application:Checking Insured ID':
      case 'TLIC Application:Checking Owner ID':
      case 'Accelerated Application:Determine Underwriting Class':
      case 'TLIC Application:Initial Underwriting':
      case 'License-verification:Verifying Fluidless':
      case 'ESign:Resetting ESign Data':
        if (altSubmitSignVerbiageEnabled) {
          return 'Almost there!\nNext: sign your application.';
        }
        return 'We’re processing your application.';
      case 'TLIC Application:Gathering Data':
        if (idState === 'HI') {
          return 'We need more time to process your application.';
        } else {
          if (altSubmitSignVerbiageEnabled) {
            return 'Almost there!\nNext: sign your application.';
          }
          return 'We’re processing your application.';
        }
      case 'Application Declined:With Vantage Option':
      case 'Application Declined:Uninsurable':
      case 'Application Rejected:Uninsurable':
        return 'Sorry, we are unable to approve your application.';
      case 'Application Rejected:ID Verification Failed':
      case 'Application Rejected:MVR Verification Failed':
      case 'Application Rejected:With Vantage Option':
        return 'Sorry, we are unable to process your application.';
      case 'Application Canceled:Without TLIC':
      case 'Application Canceled By User:Without TLIC':
      case 'Application Canceled:No Labs':
      case 'Application Canceled:Not Accepted':
      case 'Application Canceled:Ran out of Time':
      case 'Funding Pause Canceled:Ran out of Time':
      case 'Terminated:Canceled':
        return 'Your application has been cancelled.';
      case 'Terminated:Lapsed':
        return 'Your policy has lapsed';
      case 'Terminated:Not Taken':
      case 'Terminated:Canceled - Free Look':
        return 'Your policy has been cancelled at your request.';
      case 'Terminated:Conversion_completed':
        return 'Congratulations, your Term Life policy has been successfully converted to Whole Life policy!';
      case 'Terminated:Rescinded':
        return 'Your policy has been rescinded.';
      case 'Timeout:':
      case 'License-verification:Invalid':
      case 'License-verification:Fluidless Invalid':
        return 'Sorry, we need some more time.';
      case 'Operations Review:Operations Review':
        return 'We need more time to review your application.';
      case 'Upload Document:Upload Document':
        return 'Please upload requested documents.';
      case 'Review Document:Review Document':
        return 'Your documents are under review.';
      case 'Validate Coverpath Policies:Verifying application data':
        if (this.hasSubmissionValidationFailed) {
          return 'Sorry, we need some more time.';
        } else {
          if (altSubmitSignVerbiageEnabled) {
            return 'Almost there!\nNext: sign your application.';
          }
          return 'We’re processing your application.';
        }
        break;
      /**
       * Annuity Workflow Stages
       */
      case 'Canceled:Canceled':
        return `Your application has been cancelled by your financial professional.`;
      case 'Suitability Rejected':
        return `Sorry, we are unable to approve your application.`;
      case 'Canceled:Timeout':
        return `Your application has been cancelled.`;
      default:
        return 'Sorry, we need some more time.';
    }
  }

  private getBodyText(workflowStage: string): string {
    const [isEsignError, ownerSignComplete, txtEsignError] = this.getEsignStatus();
    const policy = this.controller.customer.application.primaryPolicy;
    const insuredPerson = policy.isAnnuity() ? policy.annuitant : policy.insured.person;
    const idState = insuredPerson.idState;
    switch (workflowStage) {
      case 'Consent To Underwriting:Consent To Underwriting':
        this.showExitCTA = true;
        const insuredEmail = insuredPerson.email ? ` (${insuredPerson.email})` : '';
        if (this.controller.customer.application.primaryPolicy.insured.hasSigner) {
          return `We've emailed the signer${insuredEmail} asking for consent for underwriting.
          Once the signer consents to underwriting on behalf of the insured, we'll submit this application automatically.`;
        } else {
          return `We've emailed the insured${insuredEmail} asking for consent for underwriting.
          Once the insured consents to underwriting, we'll submit this application automatically.`;
        }
      case 'Tele CMI:Tele CMI Needed':
        this.showExitCTA = true;
        const isOwnerSameAsInsured: boolean = policy.isOwnerSameAsInsured();
        return (
          `Please contact your financial professional so he/she may assist in 
          scheduling this phone interview at a time that is convenient for you.` +
          `<br/><br/>You completed the online portion of your application.`
        );
      case 'Loading:loading':
      case 'Consent To Underwriting:Check Underwriting Consent Requirements':
      case 'Tele CMI:Check Tele CMI Requirements':
      case 'License-verification:Verifying':
      case 'Send to Underwriting:Send to underwriting':
      case 'Underwriting:Underwriting':
      case 'TLIC Application:Determine Underwriting Class':
      case 'TLIC Application:Checking ID':
      case 'TLIC Application:Checking Insured ID':
      case 'TLIC Application:Checking Owner ID':
      case 'Accelerated Application:Determine Underwriting Class':
      case 'License-verification:Verifying Fluidless':
        return (
          'Hang tight while we review your application and gather some additional information. ' +
          'This should only take a few minutes.'
        );
      case 'TLIC Application:Initial Underwriting':
        return "We're almost finished and will have a result for you soon.";
      case 'TLIC Application:Gathering Data':
        if (idState === 'HI') {
          this.showExitCTA = true;
          return (
            "As a part of our underwriting process, we verify the insured's identity " +
            "with the provided driver's license" +
            '<br/><br/>' +
            'The state of Hawaii takes extra time to confirm license information. ' +
            "We'll email you once this is done."
          );
        } else {
          return "We're almost finished and will have a result for you soon.";
        }
      case 'Application Declined:With Vantage Option':
      case 'Application Declined:Uninsurable':
      case 'Application Rejected:Uninsurable':
        this.showDeclinedCTA = true;
        break;
      case 'Application Rejected:ID Verification Failed':
        this.showRejectedCTA = true;
        this.rejectedCause = 'identity';
        break;
      case 'Application Rejected:MVR Verification Failed':
        this.showRejectedCTA = true;
        this.rejectedCause = 'personal information';
        break;
      case 'Application Canceled:Ran out of Time':
      case 'Funding Pause Canceled:Ran out of Time':
      case 'Application Rejected:With Vantage Option':
        this.showContactCTA = true;
        break;
      case 'Application Canceled:Without TLIC':
        return 'We did not receive your eSignature in time.';
      case 'Application Canceled By User:Without TLIC':
        return 'Your life insurance application has been cancelled at your request.';
      case 'Application Canceled:No Labs':
        return 'We did not receive your medical exam in time.';
      case 'Application Canceled:Not Accepted':
        return 'The offer was not accepted in time.';
      case 'Terminated:Lapsed':
        this.showLapsedCTA = true;
        break;
      case 'Terminated:Canceled':
      case 'Terminated:Not Taken':
        this.showTerminatedCTA = true;
        break;
      case 'Terminated:Canceled - Free Look':
        return "You will receive a refund of your first month's premium.";
      case 'Terminated:Conversion_completed':
        return 'To start a new application for a term life policy, please request a quote or invitation from your advisor.';
      case 'Terminated:Rescinded':
        return 'We’ll be in touch and share additional details about the termination of your policy.';
      case 'Timeout:':
      case 'License-verification:Invalid':
      case 'License-verification:Fluidless Invalid':
        return "But you don't have to wait around. We will email you when your results are ready.";
      case 'Operations Review:Operations Review':
        this.showChatCTA = true;
        return this.getReviewMessage();
      case 'Upload Document:Upload Document':
        this.showChatCTA = false;
        this.showAddFileCTA = true;
        return `Our customer support team needs additional documents in order to proceed with your application. Please upload the requested documents below. If you have any questions, please contact customer support at ${this.contactNumber} or chat with us now.`;
      case 'Review Document:Review Document':
        this.showAddFileCTA = false;
        this.showChatCTA = true;
        return `Your uploaded documents are currently under review, and our customer support team will reach out to you shortly with next steps. If you have any questions, please contact customer support at ${this.contactNumber} or chat with us now.`;
      case 'TLIC Application:Waiting for Signature':
      case 'Accelerated Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
        if (isEsignError) {
          this.showExitCTA = true;
          // SSN validation failure error capture as part of HL-41263
          if (txtEsignError && txtEsignError === 'RECIPIENT_HAS_FAILED_SECURITY_CHECK') {
            return `Please call our customer support team at ${this.coverpathSupportPhone}.`;
          }
          return (
            "We’ll look into it, but you don't have to wait around.\n" +
            `You can call customer support at ${this.coverpathSupportPhone}`
          );
        } else {
          if (ownerSignComplete) {
            return 'We are processing your signed packet.';
          }
          return 'This will take about one minute.';
        }
      case 'Validate Coverpath Policies:Verifying application data':
        if (this.hasSubmissionValidationFailed) {
          this.showExitCTA = true;
          return `But you don't have to wait around. We will email you when your results are ready. If you need assistance, please contact our expert support operations team at ${this.contactEmail} or call us directly at ${this.contactNumber} (9am-6pm EST, Monday-Friday).`;
        } else {
          this.showExitCTA = false;
          return 'This should only take a few minutes.';
        }
      /**
       * Annuity Workflow Stages
       */
      case 'Canceled:Canceled':
        this.showExitCTA = true;
        this.showChatCTA = false;
        break;
      case 'Suitability Rejected':
        this.showExitCTA = true;
        this.showChatCTA = false;
        break;
      case 'Canceled:Timeout':
        this.showExitCTA = true;
        this.showChatCTA = false;
        break;
      default:
        return `But you don't have to wait around. We will email you when your results are ready. If you need assistance, please contact our expert support operations team at ${this.contactEmail} or call us directly at ${this.contactNumber} (9am-6pm EST, Monday-Friday).`;
    }
  }

  private getReviewMessage() {
    const businessHourMsg: string = `We're sorry, we're having trouble verifying some information. We'll look into it, but you can also call customer support at ${this.contactNumber} or chat with us now.`;
    return businessHourMsg;
  }

  private displayFooterContent(workflowStage: string): void {
    const [isEsignError, ownerSignComplete] = this.getEsignStatus();
    switch (workflowStage) {
      case 'Application Canceled:Without TLIC':
      case 'Application Canceled By User:Without TLIC':
      case 'Application Canceled:No Labs':
      case 'Application Canceled:Not Accepted':
      case 'Application Canceled:Ran out of Time':
      case 'Funding Pause Canceled:Ran out of Time':
      case 'Terminated:Canceled - Free Look':
      case 'Terminated:Conversion_completed':
      case 'Terminated:Canceled':
      case 'Terminated:Not Taken':
        this.showReapplyCTA = true;
        break;
      case 'Validate Coverpath Policies:Verifying application data':
        this.showEditCTA = false;
        break;
      case 'Consent To Underwriting:Consent To Underwriting':
      case 'Tele CMI:Tele CMI Needed':
        this.showEditCTA = true;
        break;
      /**
       * Annuity Workflow Stages
       */
      case 'Canceled:Canceled':
        this.showReapplyCTA = false;
        this.showEditCTA = false;
        break;
      case 'Suitability Rejected':
        this.showReapplyCTA = false;
        this.showEditCTA = false;
        break;
      case 'Canceled:Timeout':
        this.showReapplyCTA = false;
        this.showEditCTA = false;
        break;
      case 'TLIC Application:Waiting for Signature':
      case 'Accelerated Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
        if (!isEsignError && !ownerSignComplete) {
          this.showSupportFooter = true;
        } else {
          this.showSupportFooter = false;
        }
      default:
        break;
    }
    const policy = this.controller.customer.application.primaryPolicy;
    if (policy.isMMDirectPolicy()) {
      this.showReapplyCTA = false;
    }
    if (this.controller.nbDecommissionPost30Days) {
      this.showReapplyCTA = false;
    }
  }

  //Changing signature to return the exact text of the error
  private getEsignStatus(): [boolean, boolean, string] {
    const policy = this.controller.customer.application.primaryPolicy;
    const metadataObj = policy.getOwnerSigningPerson().esignMetadata;
    const isEsignError: boolean = metadataObj && metadataObj.error;
    const ownerSignComplete: boolean = metadataObj && metadataObj.ownerSignComplete;
    const txtEsignError = isEsignError ? metadataObj.error : undefined;
    return [isEsignError, ownerSignComplete, txtEsignError];
  }

  public uploadDocument(): void {
    this.accountCenterService.activateDocumentUploadModal();
  }

  openMessenger() {
    this.windowService.nativeWindow.Genesys(
      'command',
      'Messenger.open',
      {},
      (o) => {},
      (o) => {
        // if rejected
        this.windowService.nativeWindow.Genesys('command', 'Messenger.close');
      }
    );
  }
  private constructCard(workflowStage: string): void {
    this.setProcessingStatus(workflowStage);
    this.headerText = this.getHeaderText(workflowStage);
    this.bodyText = this.getBodyText(workflowStage);
    this.displayFooterContent(workflowStage);
  }

  async reapply(): Promise<void> {
    const responseType = await this.accountCenterService.reapplyRouteToApplication();
    if (!responseType.includes(ClientReapplyResponseType.SUCCESS)) {
      const insuredName = this.controller.customer.application.primaryPolicy.insured.person.fullName;
      const errorMessage = responseType.includes(ClientReapplyResponseType.INACTIVE_PRIMARY_ADVISOR)
        ? `Couldn't reapply the ${insuredName} case.
          The advisor on this case is no longer active. Please reach out to Customer Support.`
        : `Couldn't reapply application. Please reach out to Customer Support.`;

      this.toastService.showCustomToast({
        title: errorMessage,
        options: {
          actionType: CloseActionTypes.CLOSE,
          intent: ToasterIntent.DANGER,
          duration: 10000,
        },
      });
    }
  }

  cancelAndReapply(): void {
    this.accountCenterService.cancelAndReapply();
  }

  showInsuredConsentMock(): boolean {
    const policy = this.controller.customer.application.primaryPolicy;
    const isInIC1 = policy.workflowState === 'Consent To Underwriting';
    return this.controller.nonProdMode && isInIC1;
  }

  mockInsuredConsent(): void {
    this.controller.insuredAcknowledgementForAutomation();
  }

  async updateHasSubmissionValidationFailed(policy: Policy): Promise<void> {
    const result = await this.workflowService.getWorkflowActivityResult(policy, 'VC1A1 Validate Coverpath Policies');
    this.hasSubmissionValidationFailed = result === 'Error';

    if (this.hasSubmissionValidationFailed) {
      this.constructCard(this.currentWorkflowStage);
    }
  }
}
